import * as $ from 'jquery';
import 'jqueryCokie';

export class CookieManager {
  private static authToken = 'token';
  private static refreshToken = 'refreshToken';
  private static adminToken = 'adminToken';
  private static sbiDesigner = 'sbiDesigner';

  /**
   * Sets a cookie with a name, value, and optional attributes.
   * @param {string} name - The name of the cookie.
   * @param {string} value - The value of the cookie.
   * @param {object} [options={}] - An object of cookie attributes.
   * @param {number} [options.days] - The number of days until the cookie expires.
   * @param {string} [options.path] - The path for the cookie. Defaults to '/'.
   * @param {string} [options.domain] - The domain for the cookie.
   * @param {boolean} [options.secure] - If true, the cookie will only be sent over HTTPS.
   * @param {string} [options.sameSite] - The SameSite attribute ('Strict', 'Lax', 'None').
   */
  static set(name, value, options: { days?: number, domain?: string, secure?: boolean, sameSite?: string, path?: string } = {}) {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (options.days) {
      const date = new Date();
      date.setTime(date.getTime() + options.days * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    cookieString += `; path=${options.path || '/'}`;

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += `; secure`;
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Retrieves the value of a cookie by its name.
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} The value of the cookie, or null if not found.
   */
  static get(name) {
    const nameEQ = `${encodeURIComponent(name)}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  /**
   * Checks if a cookie with the given name exists.
   * @param {string} name - The name of the cookie to check.
   * @returns {boolean} True if the cookie exists, false otherwise.
   */
  static has(name) {
    return this.get(name) !== null;
  }

  /**
   * Removes a cookie by its name.
   * @param {string} name - The name of the cookie to remove.
   * @param {object} [options={}] - An object of cookie attributes that the cookie was set with.
   * @param {string} [options.path] - The path the cookie was set with. Defaults to '/'.
   * @param {string} [options.domain] - The domain the cookie was set with.
   */
  static remove(name, options = {}) {
    // To delete a cookie, we set its expiration date to a past date.
    this.set(name, '', { ...options, days: -1 });
  }

  /**
   * Retrieves all cookies as an object.
   * @returns {object} An object containing all cookies as key-value pairs.
   */
  static getAll() {
    const cookies = {};
    if (document.cookie === '') {
      return cookies;
    }

    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      const cookie = ca[i].trim();
      const [name, value] = cookie.split('=', 2);
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
    return cookies;
  }

  public static SetValue(name: string, value: string) {
    this.set(name, value, { days: 7 });
  }

  public static GetValue(name: string): any {
    return this.get(name);
  }

  public static Remove(name: string) {
    this.remove(name);
  }

  private static isSecureContext(): boolean {
    return typeof location !== 'undefined' && location.protocol === 'https:';
  }

  public static GetAuthToken() {
    return this.get(this.authToken);
  }

  public static SetAuthToken(token: string) {
    // Secure + SameSite=None are only valid over HTTPS; a browser silently drops
    // such a cookie on http://, so fall back to a plain cookie in http mode.
    const secure = this.isSecureContext();
    this.set(this.authToken, token, secure ? { days: 14, secure: true, sameSite: 'None' } : { days: 14 });
  }

  public static RemoveAuthToken() {
   if (this.isSecureContext()) {
     this.remove(this.authToken, { secure: true, sameSite: 'None' });
   }
   this.remove(this.authToken, { path: '/' });
  }

  public static GetRefreshToken() {
    return this.get(this.refreshToken);
  }

  public static SetRefreshToken(token: string) {
   this.set(this.refreshToken, token, { days: 28 });
  }

  public static RemoveRefreshToken() {
    this.remove(this.refreshToken);
  }

  public static GetAdminToken() {
    return this.get(this.adminToken);
  }

  public static SetAdminToken(token: string) {
    this.set(this.adminToken, token, { days: 14 });
  }

  public static RemoveAdminToken() {
    this.remove(this.adminToken);
  }

  public static GetSBIDesigner() {
    return this.get(this.sbiDesigner);
  }

  public static SetSBIDesigner(sbiDesigner: string) {
   this.set(this.sbiDesigner, sbiDesigner, { days: 7 });
  }

  public static RemoveTokens() {
    this.RemoveAuthToken();
    this.RemoveRefreshToken();
    this.RemoveAdminToken();
    this.Remove(this.sbiDesigner);
  }
}
